import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowDown,
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUp,
  ArrowUpFromLine,
  BarChart3,
  BarChartHorizontal,
  Hourglass,
  LayoutDashboard,
  Table2,
  TrendingDown,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/ui/badge'
import { Card, CardTitle } from '@/shared/ui/card'
import { PageHeader } from '@/widgets/app-shell/page-header'

/**
 * Catálogo de relatórios — PURO frontend, sem endpoint próprio (o nibo usa esta
 * tela como índice, não como relatório).
 *
 * O `to` separa o que é navegável do que é vitrine: relatório com rota E
 * back-end vira Link; o que o back-end ainda não serve aparece como card normal,
 * NÃO clicável, com o selo "Em breve". Nada aqui inventa uma tela que não existe
 * — os sem-rota só anunciam a intenção, sem levar a lugar nenhum.
 */
type ItemKey =
  | 'dashboard'
  | 'verticalAnalysis'
  | 'horizontalAnalysis'
  | 'receivableAging'
  | 'payableAging'
  | 'payable'
  | 'paid'
  | 'receivable'
  | 'received'
  | 'unifiedEntry'
  | 'unifiedSchedule'
  | 'transfers'
  | 'lostReceipts'

interface CatalogItem {
  readonly key: ItemKey
  readonly icon: LucideIcon
  /** Ausente = sem rota/back-end → card "Em breve", não navegável. */
  readonly to?: string
}

// Ordem espelha o layout de 2 colunas do print: o grid preenche linha a linha,
// então cada par aqui é uma linha (esquerda | direita) da tela do nibo.
const PERFORMANCE: readonly CatalogItem[] = [
  { key: 'dashboard', icon: LayoutDashboard, to: '/relatorios/painel-de-acompanhamento' },
  { key: 'receivableAging', icon: Hourglass },
  { key: 'verticalAnalysis', icon: BarChart3 },
  { key: 'payableAging', icon: Hourglass },
  { key: 'horizontalAnalysis', icon: BarChartHorizontal },
]

const ENTRIES: readonly CatalogItem[] = [
  { key: 'payable', icon: ArrowDownToLine, to: '/relatorios/contas-a-pagar' },
  { key: 'paid', icon: ArrowDown, to: '/relatorios/contas-pagas' },
  { key: 'receivable', icon: ArrowUpFromLine, to: '/relatorios/contas-a-receber' },
  { key: 'received', icon: ArrowUp, to: '/relatorios/contas-recebidas' },
  { key: 'unifiedEntry', icon: Table2 },
  { key: 'unifiedSchedule', icon: Table2 },
  { key: 'transfers', icon: ArrowLeftRight, to: '/relatorios/transferencias' },
  { key: 'lostReceipts', icon: TrendingDown },
]

export const Route = createFileRoute('/_authed/relatorios/mais-relatorios')({
  component: Page,
})

function Page() {
  const { t } = useTranslation('nav')

  return (
    <>
      <PageHeader title={t('reports.more')} />
      <div className="flex flex-col gap-4 p-6">
        <CatalogTitle />
        <ReportGroup titleKey="performance" items={PERFORMANCE} />
        <ReportGroup titleKey="entries" items={ENTRIES} />
      </div>
    </>
  )
}

function CatalogTitle() {
  const { t } = useTranslation('reports')
  return <h2 className="text-ink text-lg font-semibold">{t('catalog.title')}</h2>
}

function ReportGroup({
  titleKey,
  items,
}: {
  readonly titleKey: 'performance' | 'entries'
  readonly items: readonly CatalogItem[]
}) {
  const { t } = useTranslation('reports')

  return (
    <Card>
      <CardTitle className="border-line border-b px-4 py-3 text-base">
        {t(`catalog.groups.${titleKey}`)}
      </CardTitle>
      <ul className="grid gap-1 p-3 sm:grid-cols-2">
        {items.map((item) => (
          <li key={item.key}>
            <CatalogCard item={item} />
          </li>
        ))}
      </ul>
    </Card>
  )
}

function CatalogCard({ item }: { readonly item: CatalogItem }) {
  const { t } = useTranslation(['reports', 'common'])
  const Icon = item.icon
  const title = t(`catalog.items.${item.key}.title`)
  const description = t(`catalog.items.${item.key}.description`)

  const body = (
    <>
      <span className="bg-action-bg text-action grid size-9 shrink-0 place-items-center rounded-md">
        <Icon className="size-5" aria-hidden />
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="text-ink flex flex-wrap items-center gap-2 font-medium">
          {title}
          {item.to === undefined && <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>}
        </span>
        <span className="text-ink-muted text-xs leading-snug">{description}</span>
      </span>
    </>
  )

  if (item.to !== undefined) {
    return (
      <Link
        to={item.to}
        className="hover:bg-surface-2 flex items-start gap-3 rounded-md p-3 transition-colors"
      >
        {body}
      </Link>
    )
  }

  // Não é <button disabled>: não há ação nenhuma a desabilitar — é um anúncio.
  // aria-disabled marca o estado para o leitor de tela sem prometer clique.
  return (
    <div aria-disabled className="flex items-start gap-3 rounded-md p-3">
      {body}
    </div>
  )
}
